import React, { useCallback } from 'react';

import { DEVICE_TYPE, getSearchFacadeUrl } from 'config/api';

import { SearchFacadeResponse } from 'store/railsBuilder/reducer.types';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';

import { CollectionItemsList } from 'screens/Collection/types';
import { SearchOptions, SearchType } from 'screens/Search/types';

type Params = {
    searchQuery: string | undefined;
    searchType: SearchType | undefined;
    searchOptions: SearchOptions | undefined;
    setData: React.Dispatch<React.SetStateAction<CollectionItemsList>>;
    setTotalItems: React.Dispatch<React.SetStateAction<number>>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
};

export const SEARCH_LIMIT = 50;

function useLoadSearch({
    searchQuery,
    searchType,
    searchOptions,
    setData,
    setTotalItems,
    setPage,
}: Params) {
    const onLoadSearch = useCallback(async () => {
        if (searchQuery && searchType) {
            try {
                // get data by `/search/v1/search` API
                const { data: searchResponse } =
                    await NagraInstance.get<SearchFacadeResponse>(
                        getSearchFacadeUrl({
                            q: searchQuery,
                            type: searchType,
                            page: 1,
                            device: DEVICE_TYPE,
                            limit: SEARCH_LIMIT,
                            ...searchOptions,
                        }),
                    );

                setData(searchResponse.results);
                setTotalItems(searchResponse.totalRecords);
                setPage(1);
            } catch (e) {
                setData([]);
                setTotalItems(0);
                setPage(1);
            }
        }
    }, [
        searchOptions,
        searchQuery,
        searchType,
        setData,
        setPage,
        setTotalItems,
    ]);

    const onLoadSearchNextPage = useCallback(
        async (nextPage: number) => {
            if (searchQuery && searchType) {
                const { data: searchResponse } =
                    await NagraInstance.get<SearchFacadeResponse>(
                        getSearchFacadeUrl({
                            q: searchQuery,
                            type: searchType,
                            page: nextPage,
                            device: DEVICE_TYPE,
                            limit: SEARCH_LIMIT,
                            ...searchOptions,
                        }),
                    );

                // @ts-ignore
                setData((prevData) => [...prevData, ...searchResponse.results]);
                setTotalItems(searchResponse.totalRecords);
                setPage(nextPage);
            }
        },
        [
            searchOptions,
            searchQuery,
            searchType,
            setData,
            setPage,
            setTotalItems,
        ],
    );

    return {
        onLoadSearch,
        onLoadSearchNextPage,
    };
}

export default useLoadSearch;
